import { TRACK_SWITCH_ACKNOWLEDGEMENT } from '@/components/SwitchTrackModal/trackTransition';
import { CurrentUserContext } from '@/contexts/contexts';
import { Track } from '@bariendo/lib/constants';
import { PacWidget } from '@bariendo/lib/patient-access-script';
import { answerString, PatientAccessCallAnswers } from '@bariendo/lib/patient-access-note';
import {
  describeVerificationTask,
  insuranceTravelMarket,
  PN_INSURANCE_TRAVEL_ID,
  verificationTaskValue,
} from '@bariendo/lib/pn-consultation-script';
import { Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useContext } from 'react';
import {
  InsuranceCardDetails,
  insuranceTaskDescription,
  writeInsuranceCoverage,
  writeInsuranceTravelLocation,
} from './insuranceVerification';
import { moveToFinancingApproval } from './financingApproval';
import { INSURANCE_VERIFICATION_TASK_TITLE } from '@bariendo/lib/coverage-verification-task';
import { useCoverageVerification } from './useCoverageVerification';
import { VerificationTaskButton, VerificationTaskDone } from './VerificationTaskButton';

interface InsuranceVerificationTaskProps {
  widget: Extract<PacWidget, { kind: 'verificationTask' }>;
  /** The answers that apply. */
  answers: PatientAccessCallAnswers;
  /** Records what the button did, and saves it at once so a retry can't repeat it. */
  onDone: (id: string, value: string) => void;
  patient?: Patient;
  readOnly?: boolean;
  /**
   * Hands the Coverage write the member ID and group number the card rows
   * hold because the patient had no insurance Coverage to save them to, read
   * at the moment of writing (`InsuranceCard.writeHeld`). They go to the
   * Coverage the switch creates or adopts; never to the note or the task.
   */
  withHeldCardDetails?: (write: (held: InsuranceCardDetails) => Promise<void>) => Promise<void>;
  /** v2.0 (BAR-3002): the button also sets the Location and moves the patient to Financing and approval. */
  financing?: boolean;
}

/**
 * The insurance section's next step (BAR-2922): "Move to Insurance track and
 * create verification task", or "Create verification task" when the patient
 * is already on the Insurance track. Only shown once the patient meets the
 * criteria.
 *
 * Between the switch and the task it writes the insurer and source, and any
 * member ID and group number the card rows held, to the Coverage (which only
 * exists once the patient is on the Insurance track) and, on the move, sets
 * the Intake and billing Location to the travel location. Both are safe to
 * repeat, so a retry after a failure re-runs them and creates only the
 * missing task.
 */
export function InsuranceVerificationTask({
  widget,
  answers,
  onDone,
  patient,
  readOnly,
  withHeldCardDetails,
  financing = false,
}: InsuranceVerificationTaskProps) {
  const medplum = useMedplum();
  // `useContext`, not `useCurrentUser`: no provider means no role.
  const canSwitch = useContext(CurrentUserContext)?.currentUserPermissions.canAccessPayments ?? false;
  const { trackLoading, switching, needsAcknowledgement, run } = useCoverageVerification(
    readOnly ? undefined : patient,
    Track.INSURANCE
  );

  // The target never changes here, unlike the employer button's, so done is done.
  const done = describeVerificationTask(answers.values[widget.id], 'Insurance');
  if (done) return <VerificationTaskDone text={done} />;
  if (readOnly) return null;

  return (
    <VerificationTaskButton
      label={
        financing
          ? switching
            ? 'Move to Insurance track and Financing and approval'
            : 'Move to Financing and approval'
          : switching
            ? 'Move to Insurance track and create verification task'
            : 'Create verification task'
      }
      unavailableText={canSwitch ? undefined : 'Payments role needed to move the track or create the task'}
      acknowledgementLabel={needsAcknowledgement ? TRACK_SWITCH_ACKNOWLEDGEMENT : undefined}
      loading={trackLoading}
      onRun={async (acknowledged) => {
        if (!patient) throw new Error('No patient');
        const outcome = await run(
          { title: INSURANCE_VERIFICATION_TASK_TITLE, description: insuranceTaskDescription(answers) },
          acknowledged,
          async (moved) => {
            // The Location first, only on the move: it doesn't need the
            // Coverage, and a later failure leaves less undone. On the
            // Insurance track already, a Location someone set since stays. The
            // Intake and billing panel picks it up through the page's Patient
            // subscription.
            // v2.0 fills it only where empty, after the task (BAR-3000, BAR-3002).
            if (moved && !financing) await writeInsuranceTravelLocation(medplum, patient, answers);
            const writeCoverage = (held: InsuranceCardDetails = {}) =>
              writeInsuranceCoverage(medplum, patient, answers, held);
            await (withHeldCardDetails ? withHeldCardDetails(writeCoverage) : writeCoverage());
          }
        );
        const stage = financing
          ? await moveToFinancingApproval(
              medplum,
              patient.id as string,
              insuranceTravelMarket(answerString(answers.values[PN_INSURANCE_TRAVEL_ID]))
            )
          : undefined;
        onDone(widget.id, verificationTaskValue(outcome, Track.INSURANCE, stage));
      }}
    />
  );
}
